import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Me } from "../../shared/models";
import { api, ApiRequestError } from "../data/api";
import { Wordmark } from "../ui/Brand";
import { QrCode } from "../ui/OutsideLink";
import { Scenery } from "./Scenery";

// Signing in a screen from a phone (worker/routes/auth.ts, /auth/link/*). The
// screen shows a QR code; a family member scans it, signs in on their phone if
// they need to, and approves. The wall then needs no keyboard at all.

interface Link {
  url: string;
  pollToken: string;
  expiresAt: string;
}

const POLL_MS = 3000;

/** QR code for the sign-in card; signs this screen in once someone approves it on their phone. */
export function PhoneSignIn({ returnTo }: { returnTo: string }) {
  const [link, setLink] = useState<Link | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let stopped = false;
    let timer = 0;
    let current: Link | null = null;
    const fresh = async () => {
      try {
        current = await api<Link>("/auth/link/start", { method: "POST", body: {} });
        setFailed(false);
      } catch {
        current = null;
        setFailed(true);
      }
      if (!stopped) setLink(current);
    };
    const tick = async () => {
      // A screen nobody is looking at doesn't need a code (or the requests).
      if (document.visibilityState === "visible") {
        if (!current || Date.parse(current.expiresAt) - Date.now() < 15_000) await fresh();
        else {
          try {
            const res = await api<{ status: "waiting" | "expired" | "signed-in" }>("/auth/link/poll", { method: "POST", body: { pollToken: current.pollToken } });
            if (res.status === "signed-in") {
              window.location.replace(returnTo);
              return;
            }
            if (res.status === "expired") await fresh();
          } catch {
            /* offline for a moment; keep the code and try again */
          }
        }
      }
      if (!stopped) timer = window.setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [returnTo]);

  return (
    <div className="signin-phone">
      <p className="signin-phone-lead">Or sign in with your phone</p>
      {link ? (
        <QrCode text={link.url} className="signin-qr" label="QR code to sign in this screen from your phone" />
      ) : (
        <div className="signin-qr signin-qr-empty">{failed ? "Couldn’t get a code. Retrying…" : "…"}</div>
      )}
      <p className="muted small">Point your phone’s camera at the code, then tap “Sign it in” on your phone.</p>
    </div>
  );
}

/** /link?c=… on the phone: approve signing that screen in as me. */
export function ApproveLink({ me }: { me: Me }) {
  const code = new URLSearchParams(window.location.search).get("c") ?? "";
  const info = useQuery({
    queryKey: ["link-info", code],
    queryFn: () => api<{ device: string; expiresAt: string }>(`/auth/link/info?c=${encodeURIComponent(code)}`),
    retry: (count, err) => !(err instanceof ApiRequestError && err.status < 500) && count < 2,
    staleTime: Infinity,
  });
  const approve = useMutation({ mutationFn: () => api<{ ok: true }>("/auth/link/approve", { method: "POST", body: { code } }) });
  const error = approve.error ?? info.error;

  let body;
  if (approve.isSuccess) {
    body = (
      <>
        <h2>Done</h2>
        <p>The screen will finish signing in within a few seconds.</p>
        <a className="btn btn-lg" href="/">
          Go to Kinmantle
        </a>
      </>
    );
  } else if (error) {
    body = (
      <>
        <p className="banner banner-error">{error.message}</p>
        <a className="btn btn-lg" href="/">
          Go to Kinmantle
        </a>
      </>
    );
  } else if (!info.data) {
    body = <p className="muted">Loading…</p>;
  } else {
    body = (
      <>
        <h2>Sign in this screen?</h2>
        <p>
          <strong>{info.data.device}</strong> will be signed in to Kinmantle as <strong>{me.member.displayName}</strong>.
        </p>
        <p className="muted small">Only do this for a screen you can see in front of you: whoever is at it can use Kinmantle as you.</p>
        <button className="btn btn-primary btn-lg" disabled={approve.isPending} onClick={() => approve.mutate()}>
          {approve.isPending ? "Signing in…" : "Sign it in"}
        </button>
        <a className="btn" href="/">
          Not now
        </a>
      </>
    );
  }

  return (
    <div className="signin">
      <Scenery phase="dusk" condition="partly" season="autumn" moon={null} animate />
      <div className="signin-card">
        <Wordmark />
        {body}
      </div>
    </div>
  );
}
